import { type JanusCtx, type Row, activeStatuses, handBack, itemField, record, upper, definitionNotHeld } from './context.js';
import { ABILITY_LOCKOUT_ID } from './statuses/combat-lockout.js';

const UUID = /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/;

function entryId(entry: unknown): string {
  if (typeof entry === 'string') return upper(entry);
  const row = record(entry);
  return upper(row?.item_layer_effect ?? itemField(row, 'item_id') ?? itemField(record(row?.item_arr), 'item_id'));
}

/** ft_target_immune_to_ability_ids(): row layers and same-use status layers where the held row has not been reflattened. */
export function immuneToAbilityIds(ctx: JanusCtx, target: Row): Set<string> {
  const ids = new Set(ctx.read.entries(target, 'immune_to_ability').map(entryId).filter(Boolean));
  for (const status of activeStatuses(ctx, target)) {
    // R-172: a lockout row (immune_to_ability_after_effect_until_combat_end) bars the ability that applied it — its status_origin_id.
    if (upper(status.status_item_id ?? itemField(record(status.status_arr), 'item_id')) === ABILITY_LOCKOUT_ID) {
      const origin = upper(status.status_origin_id);
      if (origin) ids.add(origin);
      continue;
    }
    const inline = record(status.status_arr);
    const definition = inline && Object.keys(inline).some((key) => key !== 'items')
      ? inline : ctx.read.definition(upper(status.status_item_id));
    if (!definition) definitionNotHeld('immune_to_ability:status-definition-not-held', status.status_item_id);
    for (const entry of ctx.read.entries(definition, 'immune_to_ability')) {
      const id = entryId(entry);
      if (id) ids.add(id);
    }
  }
  return ids;
}

/** PHP tests the resolved item before its nested attack_ability_arr rider. */
export function targetImmuneToAbilityId(ctx: JanusCtx, ability: Row, target: Row): string | null {
  const immune = immuneToAbilityIds(ctx, target);
  if (!immune.size) return null;
  const candidates = [ability, record(ability.attack_ability_arr)].filter((row): row is Row => !!row);
  for (const row of candidates) {
    const id = upper(itemField(row, 'item_id') ?? (Array.isArray(row.item_id_used) ? row.item_id_used[0] : null));
    if (id && immune.has(id)) return id;
  }
  return null;
}

export function immuneAbilityName(ctx: JanusCtx, ability: Row, id: string): string {
  const candidates = [ability, record(ability.attack_ability_arr)].filter((row): row is Row => !!row);
  const row = candidates.find((candidate) => upper(itemField(candidate, 'item_id')) === id) ?? ctx.read.definition(id);
  if (!row) definitionNotHeld('ability_target_gate_failure:immune-ability-definition-not-held', id);
  const name = String(itemField(row, 'item_name') ?? '');
  if (!name || /[<&>"']/.test(name)) handBack('ability_target_gate_failure:html-name');
  return name;
}

export function gateItemPath(ctx: JanusCtx, ability: Row): string {
  const type = String(itemField(ability, 'item_type') ?? '');
  const out: string[] = [];
  const push = (id: unknown): void => { const s = upper(id); if (UUID.test(s) && !out.includes(s)) out.push(s); };
  if (['weapon', 'natural_weapon'].includes(type))
    push(ctx.read.inventoryField?.(ability, 'inventory_item_id') ?? itemField(ability, 'item_id'));
  const strike = record(ability._weapon_strike_weapon_arr);
  if (strike) push(ctx.read.inventoryField?.(strike, 'inventory_item_id') ?? itemField(strike, 'item_id'));
  const rider = record(ability.attack_ability_arr);
  if (rider) { push(itemField(rider, 'item_id')); if (Array.isArray(rider.item_id_used)) push(rider.item_id_used[0]); }
  if (!['weapon', 'natural_weapon'].includes(type)) push(itemField(ability, 'item_id'));
  if (Array.isArray(ability.item_id_used)) push(ability.item_id_used[0]);
  return out.length ? `ft_gate_item_path:${out.join('|')}` : '';
}
